import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';



interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; data?: any }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  checkSenhaProvisoria: (user: User) => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Log apenas mudanças importantes em desenvolvimento
        if (import.meta.env.DEV && (event === 'SIGNED_IN' || event === 'SIGNED_OUT')) {
          console.log('🔐 Auth state change:', event, session?.user?.email || 'sem usuário');
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('❌ Erro ao obter sessão:', error);
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch(error => {
      console.error('❌ Erro ao verificar sessão:', error);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error('❌ Erro no login:', error.message);
      } else if (import.meta.env.DEV) {
        console.log('✅ Login bem-sucedido:', data.user?.email);
      }
      
      return { error, data };
    } catch (error) {
      console.error('❌ Erro na operação:', error);
      return { error };
    }
  }, []);

  const signUp = async (email: string, password: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl
        }
      });
      

      
      return { error };
    } catch (error) {
      console.error('❌ Erro na operação:', error);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      console.log('🔐 Iniciando logout...');
      
      // Limpar estado local primeiro
      setUser(null);
      setSession(null);
      
      // scope: 'local' evita 403 Forbidden do Supabase ao usar signOut (global é o padrão)
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      
      if (error) {
        console.error('❌ Erro no logout:', error);
        // Não lançar erro, apenas limpar estado local
      }
      
      console.log('✅ Logout realizado com sucesso');
    } catch (error) {
      console.error('❌ Erro no logout:', error);
      // Não lançar erro, apenas limpar estado local
      setUser(null);
      setSession(null);
    }
  };

  const checkSenhaProvisoria = async (user: User): Promise<boolean> => {
    try {
      // Verificar se o usuário tem a marca de senha provisória
      const senhaProvisoria = user.user_metadata?.senha_provisoria;
      return !!senhaProvisoria;
    } catch (error) {
      console.error('❌ Erro ao verificar senha provisória:', error);
      return false;
    }
  };

  const value = useMemo(() => ({
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    checkSenhaProvisoria,
  }), [user, session, loading, signIn, signUp, signOut, checkSenhaProvisoria]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook para usar o contexto de autenticação
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
